import { GetServerSideProps } from 'next';
import { SSRConfig, useTranslation } from 'next-i18next';
import i18nextConfig from 'next-i18next.config';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useEffect, useState } from 'react';
import tw, { styled } from 'twin.macro';

const Container = styled.div`
  .pm-table-sticky-wrapper, .heading-anchor-wrapper {
    display: none;
  }
  select {
    -webkit-appearance: none;
    -moz-appearance: none;
    background-color: transparent;
    background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAHCAYAAAAxrNxjAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAB2SURBVHgBbY7RDYAgDESpEziCI7gCI/jDGrqBjIBTkMAQruAoboDXpJBKuOQC9B7NUUppN5Bz7jID1XyCPRxyzmcPySzAB4MWfkspXsN85xln8EayfsVxwzMRcWgUZFHrIdWlwTJqED+oK15ho6GhYoyLfPjpA8uNNTUq9ocbAAAAAElFTkSuQmCC);
    background-position: 88%;
    background-repeat: no-repeat;
    border: 1px solid #ececec;
    padding: 8px 50px 8px 20px;
    border-radius: 10px;
    line-height: 18px;
  }

  ${[
    tw`w-full`,
    tw`text-[#333333] text-sm leading-7`,
    tw`p-[10px 20px] mt-[20px]`,
  ]}
  padding-top: 73px;
  padding-bottom: 100px;
  
  hr {
    margin-top: 40px;
    margin-bottom: 40px;
  }

  h1, h3 {
    margin-top: 40px;
    margin-bottom: 10px;
  }
  
  h1 { 
    ${tw`text-[28px] font-bold`} 
    line-height: 200%;
    margin-bottom: 10px;
  }
  h3 { ${tw`text-[18px] leading-[24px]`} }

  p { line-height: 200%; }

  ul[data-indent-level="1"], ol[data-indent-level="1"] {
    line-height: 200%;
    padding: 10px 20px 15px 20px;
    margin-top: 10px;
    margin-bottom: 10px;
    ${tw`bg-[#F6F6F6]`}
  }
  ul {
    list-style-type: disc;
  }
  ol {
    list-style-type: decimal;
  }
  li {
    ${tw`ml-5`}
    line-height: 200%;
  }

table {
    ${tw`break1280:text-[14px] text-[12px]`}
    width: 100%;
    text-align: center;
    margin: 10px 0;
    tr {
      &: first-of-type {
        border-top: 1px solid #e6e6e6;
      }
      border-bottom: 1px solid #e6e6e6;
      td, th {
        &:not(:last-child) {
          border-right: 1px solid #e6e6e6;
        }
    }
  td {
    padding: 20px 5px
  }
}
`;

const versionList = ['20230208'].reverse();

export const getServerSideProps: GetServerSideProps<
  { html: string } & SSRConfig
> = async (ctx) => {
  const protocol =
    process.env.NODE_ENV === 'production' ? 'https://' : 'http://';
  const host = ctx.req.headers.host;
  const [lastestVersion] = versionList;
  const url =
    ctx.locale === 'en'
      ? `${protocol}${host}/policy/en/${lastestVersion}.html`
      : `${protocol}${host}/policy/${lastestVersion}.html`;
  const res = await fetch(url);
  const html = await res.text();

  return {
    props: {
      html,
      lastestVersion,
      ...(await serverSideTranslations(
        ctx.locale as string,
        ['home', 'association'],
        i18nextConfig,
      )),
    },
  };
};

function Policy({
  html,
  lastestVersion,
}: {
  html: string;
  lastestVersion: string;
}) {
  const [htmlFileString, setHtmlFileString] = useState<string>(html);
  const [currentVersion, setCurrentVersion] = useState<string>(lastestVersion);
  const { i18n } = useTranslation('association');
  const lang = i18n.resolvedLanguage;

  useEffect(() => {
    (async () => {
      const url =
        lang === 'en'
          ? `/policy/en/${currentVersion}.html?timestamp=${new Date().getTime()}`
          : `/policy/${currentVersion}.html?timestamp=${new Date().getTime()}`;
      const res = await fetch(url);
      const data = await res.text();

      setHtmlFileString(data);
    })();
  }, [currentVersion, lang]);

  return (
    <Container
      css={[
        tw`break1280:w-[1024px] break768:px-[80px] break360:px-[24px] break1280:m-auto text-[#111]`,
      ]}
    >
      <div className="wrap">
        <select onChange={(e) => setCurrentVersion(e.target.value)}>
          {versionList.map((v): JSX.Element => {
            return (
              <option key={v} value={v}>
                {v.replace(/(\d{4})(\d{2})(\d{2})/, '$1.$2.$3')}
              </option>
            );
          })}
        </select>
      </div>
      <div
        dangerouslySetInnerHTML={{
          __html: htmlFileString,
        }}
      ></div>
    </Container>
  );
}

export default Policy;
